import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { apiClient } from "../../lib/api";
import { Btn, CopyBtn } from "../../lib/primitives";
import { Icons } from "../../lib/icons";
import { OnboardingShell } from "./OnboardingShell";

/**
 * Step 3: mint the first API key.
 *
 * Hits the existing `POST /api/api-keys` (the same endpoint Keys.tsx uses)
 * with label "default" so the operator's first key is recognisable later.
 * Once minted, reveal the raw `sk_…` exactly once and require the operator
 * to confirm via "I've saved it" before advancing. A "Skip" link is offered
 * for operators who'd rather mint the key later from Keys.tsx.
 *
 * On either advance path we PATCH `step: 'done'`. Skip here advances to the
 * Done screen — distinct from "Skip onboarding" in the header, which
 * short-circuits to `/` with `skippedAt` set.
 */
export function GenerateKey() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => apiClient.apiKeys.create("default"),
    onSuccess: (res) => {
      setApiKey(res.apiKey);
      setError(null);
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to generate key");
    },
  });

  const advance = useMutation({
    mutationFn: () => apiClient.onboarding.patch({ step: "done" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding"] });
      navigate("/onboarding/done", { replace: true });
    },
  });

  return (
    <OnboardingShell current="generate_key">
      <div className="ad-eyebrow" style={{ marginBottom: 6 }}>
        Step 3 of 4 · API key
      </div>
      <h1 className="ad-h1" style={{ fontSize: 28, marginBottom: 8 }}>
        Create your first <em>API key.</em>
      </h1>
      <div className="ad-sub" style={{ marginBottom: 24 }}>
        Your server uses it to call AuthenSee. It's shown once, here, so have
        somewhere safe ready. You can create more later under API keys.
      </div>

      {!apiKey && (
        <div
          className="ad-card"
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
          }}
        >
          <Icons.Key
            size={28}
            style={{ color: "var(--rb-orange)" } as React.CSSProperties}
          />
          <div
            style={{
              fontSize: 13,
              color: "var(--ad-fg-3)",
              textAlign: "center",
              maxWidth: 360,
            }}
          >
            One click and you'll see your key. Have a secrets manager / password
            vault ready. We can't show it again.
          </div>
          <Btn
            kind="primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
            leading={<Icons.Key size={14} />}
          >
            {create.isPending ? "Generating…" : "Generate API key"}
          </Btn>
          {error && (
            <div
              style={{
                marginTop: 4,
                padding: 10,
                borderRadius: 8,
                fontSize: 13,
                background: "rgba(220, 38, 38, 0.08)",
                color: "#b91c1c",
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}

      {apiKey && (
        <div
          className="ad-card"
          style={{
            padding: 24,
            borderColor: "transparent",
            background: "var(--ad-orange-soft)",
          }}
        >
          <div className="ad-eyebrow" style={{ marginBottom: 10 }}>
            Shown once
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 16px",
              border: "1px solid var(--ad-border-2)",
              borderRadius: 12,
              background: "var(--ad-surface-1)",
              fontFamily: "var(--rb-font-mono)",
              fontSize: 13,
              color: "var(--ad-fg-1)",
              wordBreak: "break-all",
              marginBottom: 14,
            }}
          >
            <div style={{ flex: 1 }}>{apiKey}</div>
            <CopyBtn text={apiKey} label="Copy" />
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ad-fg-3)",
              marginBottom: 16,
              lineHeight: 1.55,
            }}
          >
            Paste this into your operator backend's env (typically
            <code style={{ fontFamily: "var(--rb-font-mono)" }}> AUTHENSEE_API_KEY</code>).
            We stored a salted hash; the plaintext above can't be recovered
            after you leave this page.
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 20,
        }}
      >
        <button
          type="button"
          onClick={() => advance.mutate()}
          disabled={advance.isPending}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            color: "var(--ad-fg-3)",
            fontSize: 13,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Skip, I'll create one later
        </button>
        {apiKey && (
          <Btn
            kind="primary"
            onClick={() => advance.mutate()}
            disabled={advance.isPending}
            trailing={<Icons.ArrowR size={14} />}
          >
            {advance.isPending ? "Saving…" : "I've saved it"}
          </Btn>
        )}
      </div>
    </OnboardingShell>
  );
}
